import {
	ActionButton,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	PeriodPanel,
	SectionCard,
} from '@practium/ui';
import type { CustomPanelControls, SectionTab } from '@practium/ui';
import { useCallback } from 'react';
import { PiePercentageChart, ComparisonLineChart } from '@practium/features/dashboard';
import { AnalysesPageSkeleton } from '../components/Skeletons/AnalysesPageSkeleton';
import { SoilAnalysisTable } from '../components/Tables/SoilAnalysisTable/SoilAnalysisTable';
import { PlantAnalysisTable } from '../components/Tables/PlantAnalysisTable/PlantAnalysisTable';
import type { UseAnalysesPageProps } from '../hooks/useAnalysesPage';
import { useAnalysesPage } from '../hooks/useAnalysesPage';
import type { AnalysesTab } from '../types/analyses.types';

export interface AnalysesPageProps extends UseAnalysesPageProps {
	onAddNew?: (tab: AnalysesTab) => void;
	onViewItem?: (tab: AnalysesTab, id: string) => void;
	onReportClick?: () => void;
}

export const AnalysesPage = ({
	onAddNew,
	onViewItem,
	onReportClick,
	...hookProps
}: AnalysesPageProps) => {
	const {
		activeTab,
		setActiveTab,
		filteredSoil,
		filteredPlant,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		filterGroups,
		page,
		setPage,
		totalPages,
		isLoading,
		permissions,
		analysesPieData,
		analysesLineData,
		chartMainValue,
		chartTrendValue,
		chartTrendDirection,
		analysesFooterText,
	} = useAnalysesPage(hookProps);

	const renderPeriodPanel = useCallback(
		({ close, activate }: CustomPanelControls) => (
			<PeriodPanel
				dateFrom={customDateFrom}
				dateTo={customDateTo}
				onChange={(from, to) => {
					setCustomDateFrom(from);
					setCustomDateTo(to);
					activate();
				}}
				onClose={close}
			/>
		),
		[customDateFrom, customDateTo, setCustomDateFrom, setCustomDateTo]
	);

	if (isLoading) return <AnalysesPageSkeleton />;

	const tabs: SectionTab<AnalysesTab>[] = [
		{
			key: 'soil',
			label: 'Solo',
			content: (
				<SoilAnalysisTable
					analyses={filteredSoil}
					onRowClick={onViewItem ? (id) => onViewItem('soil', id) : undefined}
				/>
			),
		},
		{
			key: 'plant',
			label: 'Foliar',
			content: (
				<PlantAnalysisTable
					analyses={filteredPlant}
					onRowClick={onViewItem ? (id) => onViewItem('plant', id) : undefined}
				/>
			),
		},
	];

	const handleReport = () => onReportClick?.();

	return (
		<ListPageLayout
			sidebarWidth="26rem"
			sidebarW1500="22rem"
			sidebarW1300="19rem"
			sidebarW1200="17rem"
			sidebar={
				<ListPageCharts>
					<ListPageChartCard>
						<PiePercentageChart
							title="Distribuição de Análises"
							subtitle="Distribuição entre análises de solo e foliares"
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={analysesPieData}
						/>
					</ListPageChartCard>
					<ListPageChartCard>
						<ComparisonLineChart
							title="Análises por Período"
							mainValue={chartMainValue}
							trendValue={chartTrendValue}
							trendLabel="vs semana anterior"
							trendDirection={chartTrendDirection}
							footerText={analysesFooterText}
							buttonText="Ver Relatório"
							onButtonClick={handleReport}
							data={analysesLineData}
							xAxisKey="day"
							line1Key="atual"
							line1Label="Semana atual"
							line2Key="anterior"
							line2Label="Semana anterior"
							tooltipTitle="Análises"
						/>
					</ListPageChartCard>
				</ListPageCharts>
			}
		>
			<SectionCard
				title="Gestão de Análises"
				subtitle="Gerencie análises de solo e foliares cadastradas no sistema"
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				search={true}
				searchQuery={searchQuery}
				onSearch={setSearchQuery}
				sortAsc={sortAsc}
				onToggleSort={toggleSort}
				filterSlot={
					<FilterChip
						values={filterValues}
						onChange={setFilterValues}
						groups={filterGroups}
						placeholder="Filtros"
						customPanels={{ 'periodo:custom': renderPeriodPanel }}
					/>
				}
				footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
				headerAction={
					<>
						{permissions.canCreate && (
							<ActionButton text="Adicionar" onClick={() => onAddNew?.(activeTab)} />
						)}
						<ActionButton text="Ver Relatório" onClick={handleReport} />
					</>
				}
			/>
		</ListPageLayout>
	);
};
